import { useState, useCallback, useMemo } from 'react'
import { ContactListItem, Scope } from '@utils'
import { Contact, usePaginateSuggestedContacts } from '@hooks'
import { useListingContactsStore } from '@store'
import { UseListingContactsResult } from '../../listingContacts/types'

// Hook principal pour le système listingContacts
export const useContactFiltering= (navigation?: any): UseListingContactsResult => {
  // États locaux
  const [scope, setScope] = useState<Scope[]>([])
  
  // Store du système listingContacts (INDÉPENDANT)
  const {
    searchText,
    localFilters,
    setSearchText,
    reset,
    hasLocalFilters,
    getActiveFiltersCount,
    getFilterSummary
  } = useListingContactsStore()
   
  // Store global des contacts (personnes)
  const { flatData: contacts, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = usePaginateSuggestedContacts({
    search: searchText,
    filters: {
      gender: localFilters.gender,
      ageRange: localFilters.ageRange,
      degree: localFilters.degree ?? undefined,
      origins: (localFilters.origins || []).map((o) => o.id),
      residence: (localFilters.residence || []).map((o) => o.id),
      residenceHistory: (localFilters.livedPlaces || []).map((o) => o.id),
      nationalities: (localFilters.nationalities || []).map((n) => n.id),
      professions: (localFilters.professions || []).map((p) => p.id),
      hashtags: (localFilters.hashtags || []).map((h) => h.id),
      languages: (localFilters.languages || []).map((l) => l.id),
    },
  })

  // Filtrage des contacts (logique locale)
  const filteredContacts = useMemo(() => {
    let filtered = contacts
     //TODO
    return filtered
  }, [contacts, searchText, localFilters, hasLocalFilters, scope])
  
  // Statistiques de filtrage
  const filterStats = useMemo(() => ({
    total: contacts.length,
    filtered: filteredContacts.length,
    hasActiveFilters: hasLocalFilters() || searchText.trim().length > 0,
    totalFiltersCount: getActiveFiltersCount() + (searchText.trim().length > 0 ? 1 : 0)
  }), [contacts.length, filteredContacts.length, hasLocalFilters, searchText, getActiveFiltersCount])

  // Actions
  const handleContactPress = useCallback((contact: Contact | ContactListItem) => {
    console.log('Contact sélectionné:', contact.fullName)
    // Navigation vers le profil du contact
    // navigate('ContactProfile', { contactId: contact.id })
  }, [])
  
  const handleFilterPress = useCallback(() => {
    if (navigation) {
      navigation.navigate('ListingContactsFilters')
    }
  }, [navigation])
  
  const handleReset = useCallback(() => {
    setSearchText('')
    reset()
  }, [setSearchText, reset])
  
  // Utilitaires
  const hasActiveFilters = useCallback(() => {
    return hasLocalFilters() || searchText.trim().length > 0
  }, [hasLocalFilters, searchText])
  
  const getActiveFiltersCountTotal = useCallback(() => {
    const baseCount = getActiveFiltersCount()
    const searchCount = searchText.trim().length > 0 ? 1 : 0
    return baseCount + searchCount
  }, [getActiveFiltersCount, searchText])
  
  const getFilterSummaryText = useCallback(() => {
    const parts: string[] = []
    
    // Ajouter la recherche
    if (searchText.trim().length > 0) {
      parts.push(`Recherche: "${searchText}"`)
    }
    
    // Ajouter le résumé des filtres
    const filterSummary = getFilterSummary()
    if (filterSummary) {
      parts.push(filterSummary)
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Aucun filtre actif'
  }, [searchText, getFilterSummary])
  
  return {
    // Données
    contacts,
    scope,
    setScope,
    // États
    searchText,
    isPending: isFetching,
    isFetchingNextPage,
    hasNextPage,
    // Statistiques
    filterStats,
    
    // Actions
    fetchNextPage,
    setSearchText,
    handleContactPress,
    handleFilterPress,
    handleReset,
    
    // Utilitaires
    hasActiveFilters,
    getActiveFiltersCount: getActiveFiltersCountTotal,
    getFilterSummary: getFilterSummaryText,
  }
} 